// Auth hook surface for components. State comes from the Zustand store;
// mutations go through TanStack Query so consumers get isPending / error /
// reset out of the box.
import { useMutation, useQuery } from '@tanstack/react-query';
import { authService } from '@core/services/authService';
import { getReferralAvailability } from '@core/services/referralService';
import { useAuthStore } from '@core/store/authStore';
import { useStoreContextStore } from '@core/store/storeContextStore';

export function useAuth() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const customer = useAuthStore((s) => s.customer);
  const accountId = useAuthStore((s) => s.accountId);
  const logout = useAuthStore((s) => s.clearSession);

  return {
    isAuthenticated: Boolean(accessToken && customer),
    accessToken,
    customer,
    accountId,
    logout,
  };
}

export function useLoginMutation() {
  return useMutation({
    mutationKey: ['auth', 'login'],
    mutationFn: authService.login,
  });
}

export function useSignupMutation() {
  return useMutation({
    mutationKey: ['auth', 'signup'],
    mutationFn: authService.signup,
  });
}

export function useReferralAvailabilityQuery(options = {}) {
  const accountId = useStoreContextStore((s) => s.accountId);

  return useQuery({
    queryKey: ['crm', 'referrals', 'availability', accountId],
    queryFn: getReferralAvailability,
    enabled: options.enabled !== false && Boolean(accountId),
  });
}

export function useVerifyOtpMutation() {
  return useMutation({
    mutationKey: ['auth', 'verifyOtp'],
    mutationFn: authService.verifyOtp,
  });
}

export function useResendOtpMutation() {
  return useMutation({
    mutationKey: ['auth', 'resendOtp'],
    mutationFn: authService.resendOtp,
  });
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationKey: ['auth', 'forgotPassword'],
    mutationFn: authService.forgotPassword,
  });
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationKey: ['auth', 'resetPassword'],
    mutationFn: authService.resetPassword,
  });
}
